<!--
  - Copyright (c) 2008-2023 by teambits GmbH, Darmstadt, Germany (http://www.teambits.de). All rights reserved.
  - This is CONFIDENTIAL code. Use is subject to license terms.
  -->
<script>
import MeetingTypesMixin from "./MeetingTypesMixin"

export default {
  name: "MeetingTemplatesMixin",

  mixins: [MeetingTypesMixin],

  data() {
    return {
      supportedMeetingTemplates: {
        MEETING: {
          type: "MEETING",
          operationMode: "SYNC",
          key: "MEETING",
          interactionDefaultAnonymous: false,
        },
        SURVEY: {
          type: "MEETING",
          operationMode: "ASYNC",
          key: "SURVEY",
          interactionDefaultAnonymous: false,
        },
        QUIZ: {
          type: "QUIZ",
          operationMode: "SYNC",
          key: "QUIZ",
          interactionDefaultAnonymous: false,
        },
        ELECTION: {
          type: "ELECTION",
          operationMode: "SYNC",
          key: "ELECTION",
          interactionDefaultAnonymous: true,
        },
        POSTAL_VOTE: {
          type: "ELECTION",
          operationMode: "ASYNC",
          key: "POSTAL_VOTE",
          interactionDefaultAnonymous: true,
        },
      },
    }
  },

  methods: {
    getMeetingTemplatesExtended() {
      let extendedTemplates = {}

      for (const supportedMeetingTemplateKey in this.supportedMeetingTemplates) {
        const supportedMeetingTemplate = this.supportedMeetingTemplates[supportedMeetingTemplateKey]
        let obj = { ...supportedMeetingTemplate, ...this.getMeetingTypeObject(supportedMeetingTemplate.type) }
        extendedTemplates[supportedMeetingTemplateKey] = obj
      }

      if (!this.$store.getters["settings/electionsEnabled"]) {
        delete extendedTemplates.ELECTION
        delete extendedTemplates.POSTAL_VOTE
      }

      return extendedTemplates
    },

    /**
     * Returns an array of meeting templates for breakout meetings that have at least one meeting room available. The
     * returned object is of the same structure as {@link this.getMeetingTemplatesExtended() getMeetingTemplatesExtended()} above.
     *
     * @param meetingId Any meeting ID that will be resolved to the corresponding main meeting
     * @param meetingCounts An optional object containing the counts of open meetings per type
     * @return {{}} An object of meeting types available for meeting types for breakout meetings or null if the
     *              data is missing to determine which types are available
     */
    getAvailableBreakoutMeetingTemplates(meetingId, meetingCounts = null) {
      let result = this.getMeetingTemplatesExtended()
      if (meetingId) {
        let mainMeeting = this.$store.getters["meetings/getMainMeetingById"](meetingId)
        if (!mainMeeting) {
          return null
        }
        let typesUsedByThisMeeting = this.getMeetingTypesSet(mainMeeting)

        if (!meetingCounts) {
          meetingCounts = this.$store.getters["meetings/getMeetingCounts"](mainMeeting.tenantId)
          if (!meetingCounts) {
            // meetingCounts not yet there
            // returning null here results in fetching meeting counts when CreateMeetingModal is shown
            return null
          }
        }

        // We don't need to care about the total meeting count here as we are in the context of breakout meetings
        // The total number of meeting rooms is relevant for main meetings only

        // To determine available types for breakout meetings remove those types that don't have at least one
        // meeting room available.
        // We remove these types also if the meeting is not running because otherwise we would need a back channel
        // on the meeting list to keep multiple MCCs in sync
        Object.values(result).forEach((template) => {
          let t = template.type.toUpperCase()
          let licensedRooms = this.$store.getters["effectiveLicenses/meetingRoomCount"](t, mainMeeting.tenantId) ?? 0
          let openMeetings = meetingCounts[t] ?? 0

          // If mainMeeting already contains a meeting of this type, decrement the open count by one because the
          // openMeetings count already contains a used room for the type used by this meeting (or any of its breakouts)
          if (mainMeeting.open && typesUsedByThisMeeting.has(t)) {
            // The main meeting already has a (breakout) meeting of this type - don't count the type twice
            openMeetings -= 1
          }

          if (openMeetings >= licensedRooms && -1 !== licensedRooms) {
            // Type not available - all licensed rooms are in use
            delete result[template.key]
          }
        })
      }

      return result
    },
  },

  i18n: {
    messages: {
      de: {
        MEETING: "Live-Meeting",
        ELECTION: "Live-Wahl",
        POSTAL_VOTE: "Online-Wahl",
        SURVEY: "Fragebogen",
        QUIZ: "Quiz",
        description: {
          MEETING:
            "In einem <b>Live-Meeting</b> werden Interaktionen für Teilnehmer von einem Moderator gesteuert und zur \
            Bearbeitung freigegeben.",
          ELECTION: "Mit einer <b>Live-Wahl</b> können von einem Moderator gesteuerte geheime Wahlen durchgeführt \
            werden.",
          POSTAL_VOTE:
            "Mit einer <b>Online-Wahl</b> können Teilnehmer selbständig durch geheime Wahlgänge navigieren und ihre \
            Stimmen abgeben.",
          SURVEY:
            "Bei einem <b>Fragebogen</b> können Teilnehmer Interaktionen selbstständig hintereinander bearbeiten.",
          QUIZ:
            "Bei einem <b>Quiz</b> werden Interaktionen für Teilnehmer von einem Moderator gesteuert und zur \
            Bearbeitung freigegeben. Dabei erhalten die Teilnehmer für die Bearbeitung Punkte.",
        },
      },
      en: {
        MEETING: "Live-Meeting",
        ELECTION: "Live-Election",
        POSTAL_VOTE: "Online-Election",
        SURVEY: "Survey",
        QUIZ: "Quiz",
        description: {
          MEETING:
            "In a <b>Live-Meeting</b>, interactions for participants are controlled by a moderator and made available \
            for contributing.",
          ELECTION: "With a <b>Live-Election</b>, secret elections controlled by a moderator can be held.",
          POSTAL_VOTE:
            "With an <b>Online-Election</b>, participants can independently navigate through secret elections and cast \
            their votes.",
          SURVEY: "With a <b>Survey</b>, participants can work on interactions independently one after the other.",
          QUIZ:
            "In a <b>Quiz</b>, interactions for participants are controlled by a moderator and made available for \
            contributing. With this, participants are able to receive points by working on interactions.",
        },
      },
    },
  },
}
</script>
